import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    CardActions,
    Grid,
    Chip,
    Rating,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    IconButton,
    Tooltip,
    Badge,
} from '@mui/material';
import { Lightbulb, PlayArrow, Star, TrendingUp, Schedule, Close, FilterList, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useReading } from '@hooks/useReading';
import { useNavigate } from 'react-router-dom';
import type { ReadingRecommendation } from '@services/reading/types';

interface RecommendationFilters {
    difficulty: string;
    targetSkill: string;
    timeAvailable: number;
}

const EnhancedRecommendations: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { readingState } = useReading();
    const [recommendations, setRecommendations] = useState<ReadingRecommendation[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState<RecommendationFilters>({
        difficulty: 'all',
        targetSkill: 'all',
        timeAvailable: 30,
    });
    const [selectedRecommendation, setSelectedRecommendation] = useState<ReadingRecommendation | null>(null);
    const [showRatingDialog, setShowRatingDialog] = useState(false);
    const [currentRating, setCurrentRating] = useState<number>(0);

    useEffect(() => {
        loadRecommendations();
    }, []);

    const loadRecommendations = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const { default: readingService } = await import('@services/reading/readingService');
            const recommendationsData = await readingService.getRecommendations();
            setRecommendations(recommendationsData);
        } catch (err) {
            setError(t('studentReading.recommendations.errors.loadingFailed'));
            console.error('Recommendations error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStartReading = (recommendation: ReadingRecommendation) => {
        navigate(`/reading/passage/${recommendation.recommended_passage.id}/practice`);
    };

    const handleRateRecommendation = async (recommendationId: string, rating: number) => {
        try {
            const { default: readingService } = await import('@services/reading/readingService');
            await readingService.rateRecommendation(recommendationId, rating);

            // Update local state
            setRecommendations((prev) =>
                prev.map((rec) => (rec.id === recommendationId ? { ...rec, user_feedback_rating: rating } : rec)),
            );
            setShowRatingDialog(false);
        } catch (err) {
            console.error('Rating error:', err);
        }
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'beginner':
            case 'elementary':
                return 'success';
            case 'intermediate':
            case 'upper_intermediate':
                return 'warning';
            case 'advanced':
            case 'expert':
                return 'error';
            default:
                return 'default';
        }
    };

    const getSkillIcon = (skill: string) => {
        switch (skill) {
            case 'pronunciation':
                return '🗣️';
            case 'fluency':
                return '⚡';
            case 'comprehension':
                return '🧠';
            case 'vocabulary':
                return '📚';
            case 'reading_speed':
                return '🏃';
            default:
                return '📖';
        }
    };

    const getPriorityBadge = (priority: number) => {
        if (priority >= 80)
            return { label: t('studentReading.recommendations.priority.high'), color: 'error' as const };
        if (priority >= 60)
            return { label: t('studentReading.recommendations.priority.medium'), color: 'warning' as const };
        return { label: t('studentReading.recommendations.priority.low'), color: 'info' as const };
    };

    const filteredRecommendations = recommendations.filter((rec) => {
        if (filters.difficulty !== 'all' && rec.recommended_passage.difficulty !== filters.difficulty) {
            return false;
        }
        if (filters.targetSkill !== 'all' && !rec.target_skills.includes(filters.targetSkill)) {
            return false;
        }
        if (rec.recommended_passage.reading_time_minutes > filters.timeAvailable) {
            return false;
        }
        return true;
    });

    if (isLoading) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('studentReading.recommendations.loading')}
                </Typography>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
                <Button onClick={loadRecommendations} sx={{ mt: 2 }}>
                    {t('common.retry')}
                </Button>
            </Paper>
        );
    }

    return (
        <Box>
            {/* Header with Filters */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
                    <Typography variant="h5" display="flex" alignItems="center" gap={1}>
                        <Lightbulb />
                        {t('studentReading.recommendations.enhanced.title')}
                    </Typography>

                    <Box display="flex" gap={2}>
                        <Tooltip title={t('studentReading.recommendations.refreshTooltip')}>
                            <IconButton onClick={loadRecommendations}>
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            startIcon={<FilterList />}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            {t('studentReading.recommendations.filters')}
                        </Button>
                    </Box>
                </Box>

                <Typography variant="body1" color="text.secondary" mb={2}>
                    {t('studentReading.recommendations.enhanced.subtitle')}
                </Typography>

                {/* Filters Section */}
                {showFilters && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('studentReading.recommendations.filters.difficulty')}</InputLabel>
                                    <Select
                                        value={filters.difficulty}
                                        onChange={(e) =>
                                            setFilters((prev) => ({ ...prev, difficulty: e.target.value }))
                                        }
                                        label={t('studentReading.recommendations.filters.difficulty')}
                                    >
                                        <MenuItem value="all">
                                            {t('studentReading.recommendations.filters.allLevels')}
                                        </MenuItem>
                                        <MenuItem value="beginner">
                                            {t('studentReading.passages.difficulty.beginner')}
                                        </MenuItem>
                                        <MenuItem value="elementary">
                                            {t('studentReading.passages.difficulty.elementary')}
                                        </MenuItem>
                                        <MenuItem value="intermediate">
                                            {t('studentReading.passages.difficulty.intermediate')}
                                        </MenuItem>
                                        <MenuItem value="upper_intermediate">
                                            {t('studentReading.passages.difficulty.upper_intermediate')}
                                        </MenuItem>
                                        <MenuItem value="advanced">
                                            {t('studentReading.passages.difficulty.advanced')}
                                        </MenuItem>
                                        <MenuItem value="expert">
                                            {t('studentReading.passages.difficulty.expert')}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('studentReading.recommendations.filters.targetSkill')}</InputLabel>
                                    <Select
                                        value={filters.targetSkill}
                                        onChange={(e) =>
                                            setFilters((prev) => ({ ...prev, targetSkill: e.target.value }))
                                        }
                                        label={t('studentReading.recommendations.filters.targetSkill')}
                                    >
                                        <MenuItem value="all">
                                            {t('studentReading.recommendations.filters.allSkills')}
                                        </MenuItem>
                                        <MenuItem value="pronunciation">
                                            {t('studentReading.practice.interface.pronunciation')}
                                        </MenuItem>
                                        <MenuItem value="fluency">
                                            {t('studentReading.practice.interface.fluency')}
                                        </MenuItem>
                                        <MenuItem value="comprehension">Comprehension</MenuItem>
                                        <MenuItem value="vocabulary">Vocabulary</MenuItem>
                                        <MenuItem value="reading_speed">Reading Speed</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('studentReading.recommendations.filters.timeAvailable')}</InputLabel>
                                    <Select
                                        value={filters.timeAvailable}
                                        onChange={(e) =>
                                            setFilters((prev) => ({ ...prev, timeAvailable: e.target.value as number }))
                                        }
                                        label={t('studentReading.recommendations.filters.timeAvailable')}
                                    >
                                        <MenuItem value={5}>5 {t('common.minutes')}</MenuItem>
                                        <MenuItem value={10}>10 {t('common.minutes')}</MenuItem>
                                        <MenuItem value={15}>15 {t('common.minutes')}</MenuItem>
                                        <MenuItem value={30}>30 {t('common.minutes')}</MenuItem>
                                        <MenuItem value={60}>1 {t('common.hour')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Paper>

            {/* Recommendations Grid */}
            <Grid container spacing={3}>
                {filteredRecommendations.map((recommendation) => (
                    <Grid item xs={12} md={6} lg={4} key={recommendation.id}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flexGrow: 1 }}>
                                {/* Priority Badge */}
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Badge
                                        badgeContent={getPriorityBadge(recommendation.priority_score).label}
                                        color={getPriorityBadge(recommendation.priority_score).color}
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                fontSize: '0.7rem',
                                                padding: '0 6px',
                                            },
                                        }}
                                    >
                                        <Box />
                                    </Badge>

                                    {recommendation.user_feedback_rating && (
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Star sx={{ fontSize: 16, color: 'gold' }} />
                                            <Typography variant="caption">
                                                {recommendation.user_feedback_rating}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* Passage Title */}
                                <Typography variant="h6" gutterBottom>
                                    {recommendation.recommended_passage.title}
                                </Typography>

                                {/* Passage Details */}
                                <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                                    <Chip
                                        label={t(
                                            `studentReading.passages.difficulty.${recommendation.recommended_passage.difficulty}`,
                                        )}
                                        color={getDifficultyColor(recommendation.recommended_passage.difficulty)}
                                        size="small"
                                    />
                                    <Chip
                                        label={`${recommendation.recommended_passage.word_count} ${t('common.words')}`}
                                        variant="outlined"
                                        size="small"
                                    />
                                    <Chip
                                        label={`${recommendation.recommended_passage.reading_time_minutes} ${t(
                                            'common.min',
                                        )}`}
                                        variant="outlined"
                                        size="small"
                                        icon={<Schedule />}
                                    />
                                </Box>

                                {/* Target Skills */}
                                <Box mb={2}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {t('studentReading.recommendations.targetSkills')}:
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        {recommendation.target_skills.map((skill, index) => (
                                            <Chip
                                                key={index}
                                                label={skill.replace('_', ' ')}
                                                size="small"
                                                icon={<span>{getSkillIcon(skill)}</span>}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                {/* Recommendation Reason */}
                                <Box mb={2}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {t('studentReading.recommendations.whyRecommended')}:
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {recommendation.recommendation_reason}
                                    </Typography>
                                </Box>

                                {/* Difficulty Adjustment */}
                                {recommendation.difficulty_adjustment !== 'same' && (
                                    <Box mb={2}>
                                        <Alert severity="info" sx={{ py: 0.5 }}>
                                            <Typography variant="caption">
                                                {t('studentReading.recommendations.adjustedTo')}{' '}
                                                {recommendation.difficulty_adjustment}{' '}
                                                {t('studentReading.recommendations.thanCurrentLevel')}
                                            </Typography>
                                        </Alert>
                                    </Box>
                                )}

                                {/* Estimated Improvement */}
                                {recommendation.estimated_improvement && (
                                    <Box mb={2}>
                                        <Typography
                                            variant="subtitle2"
                                            gutterBottom
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <TrendingUp fontSize="small" />
                                            {t('studentReading.recommendations.expectedImprovement')}:
                                        </Typography>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            {Object.entries(recommendation.estimated_improvement).map(
                                                ([skill, improvement]: [string, any]) => (
                                                    <Chip
                                                        key={skill}
                                                        label={`${skill}: +${improvement}%`}
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                ),
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>

                            <Divider />

                            <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<PlayArrow />}
                                    onClick={() => handleStartReading(recommendation)}
                                    size="small"
                                >
                                    {t('studentReading.recommendations.startReading')}
                                </Button>

                                <Box display="flex" gap={1}>
                                    <Tooltip title={t('studentReading.recommendations.rateTooltip')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSelectedRecommendation(recommendation);
                                                setCurrentRating(recommendation.user_feedback_rating || 0);
                                                setShowRatingDialog(true);
                                            }}
                                        >
                                            <Star />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}

                {filteredRecommendations.length === 0 && !isLoading && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 6, textAlign: 'center' }}>
                            <Typography variant="h6" gutterBottom>
                                {t('studentReading.recommendations.noRecommendations')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={3}>
                                {t('studentReading.recommendations.noRecommendationsDescription')}
                            </Typography>
                            <Button variant="outlined" onClick={loadRecommendations}>
                                {t('studentReading.recommendations.refreshRecommendations')}
                            </Button>
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* Rating Dialog */}
            <Dialog open={showRatingDialog} onClose={() => setShowRatingDialog(false)}>
                <DialogTitle>
                    {t('studentReading.recommendations.rateDialog.title')}
                    <IconButton
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={() => setShowRatingDialog(false)}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedRecommendation && (
                        <Box textAlign="center" py={2}>
                            <Typography variant="h6" gutterBottom>
                                {selectedRecommendation.recommended_passage.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={3}>
                                {t('studentReading.recommendations.rateDialog.question')}
                            </Typography>
                            <Rating
                                value={currentRating}
                                onChange={(event, newValue) => setCurrentRating(newValue || 0)}
                                size="large"
                                sx={{ mb: 2 }}
                            />
                            <Typography variant="caption" display="block">
                                {t('studentReading.recommendations.rateDialog.feedback')}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowRatingDialog(false)}>{t('common.cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={() =>
                            selectedRecommendation && handleRateRecommendation(selectedRecommendation.id, currentRating)
                        }
                        disabled={currentRating === 0}
                    >
                        {t('common.submit')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EnhancedRecommendations;
